





/**
 * Allows to make requests using the TomTom's
 * [Search API - Geocode](https://developer.tomtom.com/search-api/search-api-documentation-geocoding/geocode).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "Geocode/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "Geocode/go:method"}}{{/crossLink}} method to handle the response.
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * @example
 * ```js
 * function callbackFn(result) {
 *   console.log(result);
 * };
 * tomtom.geocode()
 *   .query('Amsterdam, The Netherlands')
 *   .callback(callbackFn)
 *   .go();
 * ```
 *
 * @class Geocode
 * @module Services
 * @uses Chain
 * @constructor
 * @uses KeyMixin
 * @uses QueryMixin
 * @uses TypeaheadMixin
 * @uses LimitMixin
 * @uses OffsetMixin
 * @uses LanguageMixin
 * @uses BoundingBoxMixin
 * @uses CenterMixin
 * @uses CountrySetMixin
 * @uses RadiusMixin
 * @uses BestResultMixin
 * @uses ProtocolMixin
 * @uses UnwrapMixin
 *
 * @param {Object} [options] Options to be passed to the geocoding call,
 *     or Array of such options objects to perform batch request.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 *
 * @param {String} [options.key] An API Key issued from <a href="https://developer.tomtom.com">Developer Portal</a>
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 * @param {Boolean}  [options.unwrapBbox=false] Changes the bounding box coordinates allowing longitudes that
 *  exceeds 180 degrees when is necessary. {{#crossLink "Search/unwrapBbox:method"}}More{{/crossLink}}
 *
 * @param {String} options.query Text that will be searched.
 *  This parameter is required to be defined either in the options passed to the constructor or using the
 *  **query()** method.
 * @param {Boolean} [options.storeResult=false] If is enabled the query will be interpreted as a stored geocode
 *     and will be billed according to the terms of use
 * @param {Boolean}  [options.typeahead=false] If is enabled the query will be interpreted as a
 *  partial input and the **predictive** mode will be used
 * @param {Number}   [options.limit=10] Maximum number of responses that will be returned
 * @param {Number}   [options.offset=0] Starting offset of the returned results within the full result set
 * @param {Number[]|String|L.LatLng|Object} [options.center] Location where results should be biased
 * @param {String}   [options.countrySet] List of country codes for limit the search request to those specific
 *  political limits
 * @param {Number}   [options.radius=0] The search radius in meters using the coordinates given to the **center**
 *  option as origin.
 * @param {Object|Number[]|Object[]|String} [options.boundingBox] The bounding box is an area within the search
 *  results needs to limit to. If it is omitted then the whole world will be taken into consideration.
 * @param {String}   [options.language=null] Language to use in the response.
 *  The list with all the available codes can be found
 *  <a href="https://developer.tomtom.com/search-api/search-api-documentation/supported-languages">here</a>.
 * @param {String}   [options.extendedPostalCodesFor] Represent the indexes for which extended postal codes should
 *  be included in the results
 */
define(['rest/geocode', 'model/geocode/geocode', 'services/chain', 'services/validators', 'services/converters',
    'services/worldEdge', 'model/geocode/batchGeocode', './geocodeParameters'],
function(restGeocode, model, chain, validators, converters, worldEdge, batchModel, geocodeParams) {
    'use strict';

    var fields = geocodeParams.create(),
        batchFields = {
            batchItems: {
                validator: validators.arrayOf(fields),
                converter: converters.arrayOf(fields)
            }
        };

    var geocode = restGeocode();

    function addBoundingBoxParameters(data, boundingBox) {
        data.topLeft = [boundingBox.maxLat, boundingBox.minLon];
        data.btmRight = [boundingBox.minLat, boundingBox.maxLon];
        return data;
    }

    function callbackBestResult(bestResult, callback) {
        return function(response) {
            callback(bestResult ? response[0] : response);
        };
    }

    function handleBatchChain(data, callback) {
        data.batchItems.forEach(geocodeParams.modifyRequestOptions);
        geocode.batch(geocodeParams.modifyFields(fields), data, function(results) {
            callback.call(this, batchModel(results));
        });
    }

    function geocodeQuery(fields) {
        return function(options, callback) {
            return geocode.geocode(geocodeParams.modifyFields(fields), options, callback);
        };
    }

    function handleChain(data, callback) {
        geocodeParams.modifyRequestOptions(data);
        worldEdge(geocodeQuery(fields), model, data, callbackBestResult(Boolean(data.bestResult), callback),
            addBoundingBoxParameters);
    }

    return function(options) {
        if (Array.isArray(options)) {
            return chain(batchFields, handleBatchChain, {
                batchItems: options
            });
        } else {
            return chain(fields, handleChain, options);
        }
    };
});



// WEBPACK FOOTER //
// ./services/geocode/geocode.js